import React, { useCallback, useEffect, useState } from "react";
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, CircularProgress, Alert,
    Collapse, Button,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

import { LOYALTY_API_BASE } from "../../constants";
import { loyaltyFetch } from "../../hooks/loyaltyFetch";
import { buildTransactionsByUserUrl, loyaltyBrandHeaders } from "../../utils/loyaltyCustomerApi";
import {
    TransactionStatusChip,
    getOrderNumber,
    getOrderTotal,
    getProductSummary,
    formatTxDateTime,
    filterSaleTransactions,
} from "../../utils/transactionUi";

async function fetchSaleTransactions(brandName, profileId, email = "") {
    const headers = loyaltyBrandHeaders(brandName);
    const url = buildTransactionsByUserUrl({
        brandName,
        profileId,
        email,
        transactionType: "sale",
    });
    const res = await loyaltyFetch(url, { headers });
    if (!res.ok) {
        const fallback = await loyaltyFetch(
            buildTransactionsByUserUrl({ brandName, profileId, email }),
            { headers },
        );
        if (!fallback.ok) throw new Error("Erreur lors du chargement des ventes");
        const data = await fallback.json();
        return filterSaleTransactions(Array.isArray(data) ? data : []);
    }
    const data = await res.json();
    return Array.isArray(data) ? data : [];
}

async function fetchExecutions(transactionId, brandName) {
    const res = await loyaltyFetch(
        `${LOYALTY_API_BASE}/transactions/${encodeURIComponent(transactionId)}/executions`,
        { headers: loyaltyBrandHeaders(brandName) },
    );
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data) ? data : [];
}

const ExecutionDetails = ({ transactionId, brandName }) => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [executions, setExecutions] = useState([]);
    const [error, setError] = useState(null);

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            setExecutions(await fetchExecutions(transactionId, brandName));
        } catch (err) {
            setError(err.message || "Erreur chargement exécutions");
        } finally {
            setLoading(false);
        }
    };

    const toggle = async () => {
        if (!open && executions.length === 0) {
            await load();
        }
        setOpen((v) => !v);
    };

    return (
        <Box>
            <Button size="small" onClick={toggle} sx={{ textTransform: "none", fontSize: 11, p: 0, minWidth: 0 }}>
                {open ? "Masquer règles" : "Voir règles appliquées"}
            </Button>
            <Collapse in={open}>
                {loading && <CircularProgress size={16} sx={{ mt: 1 }} />}
                {error && <Alert severity="error" sx={{ mt: 1, py: 0 }}>{error}</Alert>}
                {!loading && !error && executions.length === 0 && (
                    <Typography fontSize={11} color="text.secondary" mt={1}>Aucune exécution de règle enregistrée.</Typography>
                )}
                {executions.map((ex) => (
                    <Box key={ex.id} mt={1} p={1} bgcolor="#f8fafc" borderRadius={1} border="1px solid #e2e8f0">
                        <Typography fontSize={11} fontWeight={700}>{ex.result || "—"}</Typography>
                        {ex.details && (
                            <Typography component="pre" fontSize={10} sx={{ m: 0, mt: 0.5, whiteSpace: "pre-wrap" }}>
                                {JSON.stringify(ex.details, null, 2)}
                            </Typography>
                        )}
                        {ex.executed_at && (
                            <Typography fontSize={10} color="text.secondary">{formatTxDateTime({ processed_at: ex.executed_at })}</Typography>
                        )}
                    </Box>
                ))}
            </Collapse>
        </Box>
    );
};

/**
 * Historique des achats (transaction_type === sale) — règle backend n°3/n°4.
 */
const SaleTransactionsPanel = ({
    brandName,
    profileId,
    email = "",
    refreshToken = 0,
    showRefreshButton = true,
    onLoaded,
}) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        if (!brandName || !profileId) return;
        setLoading(true);
        setError(null);
        try {
            const sales = await fetchSaleTransactions(brandName, profileId, email);
            setItems(sales);
            onLoaded?.(sales);
        } catch (err) {
            setError(err.message || "Erreur inconnue");
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [brandName, profileId, email, onLoaded]);

    useEffect(() => {
        load();
    }, [load, refreshToken]);

    return (
        <Box>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5} flexWrap="wrap" gap={1}>
                <Typography variant="h6" fontWeight={700} fontFamily="Montserrat, sans-serif" color="#1a1a2e">
                    Historique des achats ({items.length})
                </Typography>
                {showRefreshButton && (
                    <Button
                        size="small"
                        startIcon={<RefreshIcon />}
                        onClick={load}
                        disabled={loading || !profileId}
                        sx={{ textTransform: "none" }}
                    >
                        Actualiser
                    </Button>
                )}
            </Box>

            {loading && (
                <Box display="flex" justifyContent="center" py={3}>
                    <CircularProgress size={24} />
                </Box>
            )}

            {!loading && error && (
                <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>{error}</Alert>
            )}

            {!loading && !error && (
                <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: "1px solid #e8ecf1" }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: "#f8f9ff" }}>
                                <TableCell sx={{ fontWeight: 600 }}>Commande</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Montant</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Produits</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Statut</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Traité le</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Détail</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 3, color: "text.secondary" }}>
                                        Aucune vente enregistrée.
                                    </TableCell>
                                </TableRow>
                            ) : items.map((tx) => (
                                <TableRow key={tx.id} hover>
                                    <TableCell sx={{ fontWeight: 700 }}>{getOrderNumber(tx)}</TableCell>
                                    <TableCell>{getOrderTotal(tx)}</TableCell>
                                    <TableCell sx={{ maxWidth: 180 }}>{getProductSummary(tx)}</TableCell>
                                    <TableCell>
                                        <TransactionStatusChip transaction={tx} />
                                        {String(tx?.status || "").toUpperCase() === "PROCESSED_ERRORS" && (
                                            <Typography fontSize={10} color="warning.main" mt={0.5}>
                                                Vente enregistrée, points non crédités
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>{formatTxDateTime(tx)}</TableCell>
                                    <TableCell>
                                        <ExecutionDetails transactionId={tx.id} brandName={brandName} />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export { fetchSaleTransactions };
export default SaleTransactionsPanel;
